import PropTypes from 'prop-types';
import { useRef, useEffect, memo } from 'react';
import { createChart } from 'utils';
import { ChartWrapper } from './styles';

const applyChartData = (chart, { labels, datasets, options }) => {
  // eslint-disable-next-line no-param-reassign
  chart.data.labels = labels;
  // eslint-disable-next-line no-param-reassign
  chart.data.datasets = datasets;
  // eslint-disable-next-line no-param-reassign
  chart.options = options;
  chart.update('resize');
};

export const ChartLayout = memo(
  ({ id, type, labels, datasets, options, onReady }) => {
    const ref = useRef(null);
    const chartRef = useRef(null);
    const chartDataRef = useRef({ labels, datasets, options });
    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;

    useEffect(() => {
      if (!ref.current) {
        return undefined;
      }

      let isCancelled = false;

      createChart(ref.current.getContext('2d'), type, labels, datasets, options)
        .then(chart => {
          if (isCancelled) {
            chart.destroy();
            return;
          }

          chartRef.current = chart;
          /**
           * Поки вантажився chart.js, дані могли оновитися — застосовуємо
           * найсвіжіші.
           */
          applyChartData(chart, chartDataRef.current);
          if (onReadyRef.current) {
            onReadyRef.current();
          }
        })
        .catch(() => {});

      return () => {
        isCancelled = true;

        if (chartRef.current) {
          chartRef.current.destroy();
        }
        chartRef.current = null;
      };
    }, [id, type]);

    useEffect(() => {
      chartDataRef.current = { labels, datasets, options };

      if (!chartRef.current) {
        return;
      }

      applyChartData(chartRef.current, chartDataRef.current);
    }, [labels, datasets, options]);

    return (
      <ChartWrapper>
        <canvas
          id={id}
          // eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role
          role="img"
          ref={ref}
        />
      </ChartWrapper>
    );
  }
);

ChartLayout.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  datasets: PropTypes.arrayOf(PropTypes.object).isRequired,
  options: PropTypes.objectOf(PropTypes.object).isRequired,
  onReady: PropTypes.func,
};

ChartLayout.defaultProps = {
  onReady: null,
};
